/*
 * This file is part of BlueMap, licensed under the MIT License (MIT).
 *
 * Copyright (c) Blue (Lukas Rieger) <https://bluecolored.de>
 * Copyright (c) contributors
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 * THE SOFTWARE.
 */
import { Vector2, Scene, Group } from 'three';
import  { Tile } from './Tile.js';
import {dispatchEvent, hashTile} from '../util/Utils.js';
import {getLocalStorage, setLocalStorage} from '../Utils.js';
import {TileMap} from "./TileMap";

export class TileManager {

    static tileMapSize = 100;
    static tileMapHalfSize = TileManager.tileMapSize / 2;

    /**
     * @param tileLoader {TileLoader | LowresTileLoader}
     * @param onTileLoad {function(Tile)}
     * @param onTileUnload {function(Tile)}
     * @param events {EventTarget}
     * @param options {{
     *      persistFailedTiles: boolean,
     *      failedTilesStorageKey: string,
     *      failedTilesMaxEntries: number,
     * }}
     */
    constructor(tileLoader, onTileLoad = null, onTileUnload = null, events = null, options = {}) {
        Object.defineProperty( this, 'isTileManager', { value: true } );

        this.sceneParent = new Scene();
        this.scene = new Group();
        this.sceneParent.add(this.scene);

        this.events = events;
        this.tileLoader = tileLoader;

        this.onTileLoad = onTileLoad || function(){};
        this.onTileUnload = onTileUnload || function(){};

        this.viewDistanceX = 1;
        this.viewDistanceZ = 1;
        this.centerTile = new Vector2(0, 0);

        this.currentlyLoading = 0;
        this.loadTimeout = null;

        //map of loaded tiles
        this.tiles = new Map();
        /** @type {Map<string, {x:number, z:number, retryAt:number, failures:number}>} */
        this.failedTiles = new Map();
        this.persistFailedTiles = options.persistFailedTiles !== false;
        this.failedTilesStorageKey = typeof options.failedTilesStorageKey === "string"
            ? options.failedTilesStorageKey
            : undefined;
        let failedTilesMaxEntries = Number(options.failedTilesMaxEntries);
        if (!Number.isFinite(failedTilesMaxEntries)) failedTilesMaxEntries = 5000;
        this.failedTilesMaxEntries = Math.max(100, Math.floor(failedTilesMaxEntries));
        this.persistFailedTilesTimeout = null;

        // a canvas that keeps track of the loaded tiles, used for shaders
        this.tileMap = new TileMap(TileManager.tileMapSize, TileManager.tileMapSize);

        this.unloaded = true;

        if (!this.failedTilesStorageKey) this.persistFailedTiles = false;
        this.loadFailedTilesFromStorage();
    }

    /**
     * @param x {number}
     * @param z {number}
     * @param viewDistanceX {number}
     * @param viewDistanceZ {number}
     */
    loadAroundTile(x, z, viewDistanceX, viewDistanceZ) {
        this.unloaded = false;

        let unloadTiles = false;
        if (this.viewDistanceX > viewDistanceX || this.viewDistanceZ > viewDistanceZ) {
            unloadTiles = true;
        }

        this.viewDistanceX = viewDistanceX;
        this.viewDistanceZ = viewDistanceZ;

        if (viewDistanceX <= 0 || viewDistanceZ <= 0) {
            this.removeAllTiles();
            return;
        }

        if (unloadTiles || this.centerTile.x !== x || this.centerTile.y !== z) {
            this.centerTile.set(x, z);
            this.removeFarTiles();

            this.tileMap.setAll(TileMap.EMPTY);
            this.tiles.forEach(tile => {
                if (!tile.loading && !tile.unloaded) {
                    this.tileMap.setTile(tile.x - this.centerTile.x + TileManager.tileMapHalfSize, tile.z - this.centerTile.y + TileManager.tileMapHalfSize, TileMap.LOADED);
                }
            });
        }

        this.loadCloseTiles();
    }

    unload() {
        this.unloaded = true;
        if (this.loadTimeout) {
            clearTimeout(this.loadTimeout);
            this.loadTimeout = null;
        }
        if (this.persistFailedTilesTimeout) {
            clearTimeout(this.persistFailedTilesTimeout);
            this.persistFailedTilesTimeout = null;
        }
        this.removeAllTiles();
    }

    removeFarTiles() {
        this.tiles.forEach((tile, hash, map) => {
            if (
                tile.x + this.viewDistanceX < this.centerTile.x ||
                tile.x - this.viewDistanceX > this.centerTile.x ||
                tile.z + this.viewDistanceZ < this.centerTile.y ||
                tile.z - this.viewDistanceZ > this.centerTile.y
            ) {
                tile.unload();
                map.delete(hash);
            }
        });

        let failedTilesRemoved = false;
        this.failedTiles.forEach((tile, hash, map) => {
            if (
                tile.x + this.viewDistanceX < this.centerTile.x ||
                tile.x - this.viewDistanceX > this.centerTile.x ||
                tile.z + this.viewDistanceZ < this.centerTile.y ||
                tile.z - this.viewDistanceZ > this.centerTile.y
            ) {
                map.delete(hash);
                failedTilesRemoved = true;
            }
        });
        if (failedTilesRemoved) this.schedulePersistFailedTiles();
    }

    removeAllTiles() {
        this.tileMap.setAll(TileMap.EMPTY);

        this.tiles.forEach(tile => {
            tile.unload();
        });
        this.tiles.clear();
        this.failedTiles.clear();
        this.schedulePersistFailedTiles();
    }

    loadCloseTiles = () => {
        if (this.unloaded) return;
        if (!this.loadNextTile()) {
            const retryDelay = this.getNextFailedRetryDelay();
            if (retryDelay !== null) {
                if (this.loadTimeout) clearTimeout(this.loadTimeout);
                this.loadTimeout = setTimeout(this.loadCloseTiles, Math.max(50, retryDelay));
            }
            return;
        }

        if (this.loadTimeout) clearTimeout(this.loadTimeout);

        if (this.currentlyLoading < 8) {
            this.loadTimeout = setTimeout(this.loadCloseTiles, 0);
        } else {
            this.loadTimeout = setTimeout(this.loadCloseTiles, 1000);
        }
    }

    /**
     * @returns {boolean}
     */
    loadNextTile() {
        if (this.unloaded) return false;

        let x = 0;
        let z = 0;
        let d = 1;
        let m = 1;

        while (m < Math.max(this.viewDistanceX, this.viewDistanceZ) * 2 + 1) {
            while (2 * x * d < m) {
                if (this.tryLoadTile(this.centerTile.x + x, this.centerTile.y + z)) return true;
                x = x + d;
            }
            while (2 * z * d < m) {
                if (this.tryLoadTile(this.centerTile.x + x, this.centerTile.y + z)) return true;
                z = z + d;
            }
            d = -1 * d;
            m = m + 1;
        }

        return false;
    }

    /**
     * @param x {number}
     * @param z {number}
     * @returns {boolean}
     */
    tryLoadTile(x, z) {
        if (this.unloaded) return false;

        if (Math.abs(x - this.centerTile.x) > this.viewDistanceX) return false;
        if (Math.abs(z - this.centerTile.y) > this.viewDistanceZ) return false;

        let tileHash = hashTile(x, z);
        let failedTile = this.failedTiles.get(tileHash);
        if (failedTile) {
            if (Date.now() < failedTile.retryAt) return false;
            this.failedTiles.delete(tileHash);
            this.schedulePersistFailedTiles();
        }

        let tile = this.tiles.get(tileHash);
        if (tile !== undefined) return false;

        this.currentlyLoading++;

        tile = new Tile(x, z, this.handleLoadedTile, this.handleUnloadedTile);
        this.tiles.set(tileHash, tile);
        tile.load(this.tileLoader)
            .then(() => {
                if (this.failedTiles.delete(tileHash)) this.schedulePersistFailedTiles();
                dispatchEvent(this.events, "bluemapTileLoaded", {
                    tileManager: this,
                    tile: tile
                });

                if (this.loadTimeout) clearTimeout(this.loadTimeout);
                this.loadTimeout = setTimeout(this.loadCloseTiles, 0);
            })
            .catch(error => {
                this.tiles.delete(tileHash);
                this.handleTileLoadFailure(tileHash, x, z, error);
            })
            .finally(() => {
                this.currentlyLoading--;
            });

        return true;
    }

    handleLoadedTile = tile => {
        this.tileMap.setTile(tile.x - this.centerTile.x + TileManager.tileMapHalfSize, tile.z - this.centerTile.y + TileManager.tileMapHalfSize, TileMap.LOADED);

        this.scene.add(tile.model);
        this.onTileLoad(tile);
    }

    handleUnloadedTile = tile => {
        this.tileMap.setTile(tile.x - this.centerTile.x + TileManager.tileMapHalfSize, tile.z - this.centerTile.y + TileManager.tileMapHalfSize, TileMap.EMPTY);

        this.scene.remove(tile.model);
        this.onTileUnload(tile);
    }

    /**
     * @param error {any}
     * @returns {number | undefined}
     */
    getTileLoadStatus(error) {
        const directStatus = Number(error?.response?.status ?? error?.status);
        if (Number.isFinite(directStatus)) return directStatus;

        if (typeof error?.message === "string") {
            const match = error.message.match(/\b([1-5]\d{2})\b/);
            if (match) return parseInt(match[1], 10);
        }

        return undefined;
    }

    /**
     * @param error {any}
     * @param failures {number}
     * @returns {number}
     */
    getRetryDelayForError(error, failures) {
        if (error?.status === "cancelled" || error?.name === "AbortError") return 0;

        const status = this.getTileLoadStatus(error);
        if (status === 404 || status === 410) return 24 * 60 * 60 * 1000;
        if (status === 429) return 60 * 1000;
        if (status >= 500 && status < 600) return Math.min(5 * 60 * 1000, 5000 * Math.pow(2, failures - 1));

        return Math.min(60 * 1000, 2500 * Math.pow(2, failures - 1));
    }

    /**
     * @param tileHash {string}
     * @param x {number}
     * @param z {number}
     * @param error {any}
     */
    handleTileLoadFailure(tileHash, x, z, error) {
        const previousFailures = this.failedTiles.get(tileHash)?.failures || 0;
        const failures = previousFailures + 1;
        const retryDelay = this.getRetryDelayForError(error, failures);

        if (retryDelay <= 0) {
            this.failedTiles.delete(tileHash);
            this.schedulePersistFailedTiles();
            return;
        }

        this.failedTiles.set(tileHash, {
            x,
            z,
            failures,
            retryAt: Date.now() + retryDelay,
        });
        this.enforceFailedTileEntryLimit();
        this.schedulePersistFailedTiles();
    }

    /**
     * @returns {number | null}
     */
    getNextFailedRetryDelay() {
        const now = Date.now();
        let nextRetryDelay = null;

        this.failedTiles.forEach(tile => {
            if (
                tile.x + this.viewDistanceX < this.centerTile.x ||
                tile.x - this.viewDistanceX > this.centerTile.x ||
                tile.z + this.viewDistanceZ < this.centerTile.y ||
                tile.z - this.viewDistanceZ > this.centerTile.y
            ) return;

            const remaining = tile.retryAt - now;
            if (remaining <= 0) {
                nextRetryDelay = 0;
                return;
            }

            if (nextRetryDelay === null || remaining < nextRetryDelay) {
                nextRetryDelay = remaining;
            }
        });

        return nextRetryDelay;
    }

    setPersistFailedTiles(enabled) {
        const shouldPersist = enabled === true && !!this.failedTilesStorageKey;
        if (this.persistFailedTiles === shouldPersist) return;

        this.persistFailedTiles = shouldPersist;
        if (!this.persistFailedTiles && this.persistFailedTilesTimeout) {
            clearTimeout(this.persistFailedTilesTimeout);
            this.persistFailedTilesTimeout = null;
        }
        if (this.persistFailedTiles) this.loadFailedTilesFromStorage();
    }

    loadFailedTilesFromStorage() {
        if (!this.persistFailedTiles || !this.failedTilesStorageKey) return;

        const stored = getLocalStorage(this.failedTilesStorageKey);
        if (stored === undefined || stored === null || typeof stored !== "object") return;

        const now = Date.now();
        this.failedTiles.clear();

        for (const [hash, entry] of Object.entries(stored)) {
            if (!entry || typeof entry !== "object") continue;

            const x = Number(entry.x);
            const z = Number(entry.z);
            const retryAt = Number(entry.retryAt);
            const failures = Number(entry.failures);

            if (!Number.isFinite(x) || !Number.isFinite(z)) continue;
            if (!Number.isFinite(retryAt) || retryAt <= now) continue;

            this.failedTiles.set(hash, {
                x,
                z,
                retryAt,
                failures: Number.isFinite(failures) && failures > 0 ? Math.floor(failures) : 1,
            });
        }

        this.enforceFailedTileEntryLimit();
        this.saveFailedTilesToStorage();
    }

    enforceFailedTileEntryLimit() {
        if (this.failedTiles.size <= this.failedTilesMaxEntries) return;

        const retainedEntries = [...this.failedTiles.entries()]
            .sort((a, b) => b[1].retryAt - a[1].retryAt)
            .slice(0, this.failedTilesMaxEntries);

        this.failedTiles.clear();
        retainedEntries.forEach(([hash, entry]) => {
            this.failedTiles.set(hash, entry);
        });
    }

    schedulePersistFailedTiles() {
        if (!this.persistFailedTiles || !this.failedTilesStorageKey) return;

        if (this.persistFailedTilesTimeout) clearTimeout(this.persistFailedTilesTimeout);
        this.persistFailedTilesTimeout = setTimeout(() => {
            this.persistFailedTilesTimeout = null;
            this.saveFailedTilesToStorage();
        }, 200);
    }

    saveFailedTilesToStorage() {
        if (!this.persistFailedTiles || !this.failedTilesStorageKey) return;

        this.enforceFailedTileEntryLimit();

        const now = Date.now();
        const payload = {};
        this.failedTiles.forEach((entry, hash, map) => {
            if (!entry || !Number.isFinite(entry.retryAt) || entry.retryAt <= now) {
                map.delete(hash);
                return;
            }
            payload[hash] = entry;
        });

        try {
            setLocalStorage(this.failedTilesStorageKey, payload);
        } catch (ignore) {
            // Ignore storage quota and serialization errors.
        }
    }
}
